/**
 * Общий референс проекта: сценарий и стилистика текстом плюс картинки из
 * библиотек (интерьер, действие, примеры рилсов).
 *
 * Стоит НАД раскадровкой, потому что читается раньше неё: сначала «где и как
 * мы снимаем весь рилс», потом «что происходит по кадрам». Кадры не наследуют
 * его строкой в базе — он держит рамку для человека и попадает во входы
 * генерации группой «Референсы проекта».
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ExternalLink, Plus, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { apiFetch } from "@/lib/api";
import { thumbUrl } from "@/lib/media";
import type { LibraryDetail, LibraryRow, ProjectView, Reference } from "@/lib/project";
import { errorText, groupInOrder, postJson, qk } from "@/lib/project";

import { InlineText } from "./InlineText";
import { Empty, ErrorLine, Hint, Panel, selectClass } from "./ui";

export function ProjectReferences({
  pid,
  note,
  references,
  libraries,
}: {
  pid: number;
  note: string | null;
  references: Reference[];
  libraries: LibraryRow[];
}) {
  const client = useQueryClient();
  const [libraryId, setLibraryId] = useState(0);
  const [error, setError] = useState("");

  const patch = (fields: Partial<ProjectView>) =>
    client.setQueryData<ProjectView>(qk.project(pid), (old) => (old ? { ...old, ...fields } : old));

  const saveNote = async (value: string) => {
    const result = await postJson<{ value: string }>(`/api/projects/${pid}/note`, { value });
    client.setQueryData<ProjectView>(qk.project(pid), (old) =>
      old ? { ...old, project: { ...old.project, project_note: result.value || null } } : old,
    );
    return result.value;
  };

  // Ответ — свежий список референсов проекта: кладём его как есть, а входы
  // генерации кадров (они зависят от референсов) перечитываем.
  const onRefs = (result: { references: Reference[] }) => {
    patch({ references: result.references });
    void client.invalidateQueries({ queryKey: qk.project(pid) });
  };

  const attach = useMutation({
    mutationFn: (itemId: number) =>
      postJson<{ references: Reference[] }>(`/api/projects/${pid}/references`, { reference_item_id: itemId }),
    onMutate: () => setError(""),
    onSuccess: onRefs,
    onError: (e) => setError(errorText(e)),
  });

  const detach = useMutation({
    mutationFn: (itemId: number) =>
      apiFetch<{ references: Reference[] }>(`/api/projects/${pid}/references/${itemId}`, { method: "DELETE" }),
    onMutate: () => setError(""),
    onSuccess: onRefs,
    onError: (e) => setError(errorText(e)),
  });

  const picked = new Set(references.map((r) => r.reference_item_id));

  return (
    <Panel title="Общий референс" annot="общий референс">
      <Hint>
        На что опирается весь рилс: интерьер, сценарий, стилистика. Берётся из библиотек —{" "}
        <Link to="/lib" className="text-accent-text hover:underline">
          раздел «Библиотеки»
        </Link>
        .
      </Hint>

      <InlineText
        value={note}
        multiline
        placeholder="сценарий и стилистика: что происходит, в каком настроении, чего избегаем"
        onSave={saveNote}
        className="mt-2 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-2"
        annot="сценарий проекта"
      />

      {references.length ? (
        <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-2.5">
          {references.map((r) => (
            <div key={r.reference_item_id} data-annot={`референс ${r.reference_item_id}`} className="relative grid gap-1">
              <img
                src={thumbUrl(r.asset_path)}
                alt={r.library_name}
                loading="lazy"
                className="aspect-[3/4] w-full rounded-[4px] border border-border object-cover"
              />
              <button
                type="button"
                title="убрать из проекта"
                disabled={detach.isPending && detach.variables === r.reference_item_id}
                onClick={() => detach.mutate(r.reference_item_id)}
                className="absolute top-1 right-1 grid size-6 place-items-center rounded-full bg-bg/85 text-fg-muted backdrop-blur hover:text-err disabled:opacity-50"
              >
                <X size={13} />
              </button>
              <div className="truncate text-[11.5px] text-fg-muted" title={`${r.category_name} · ${r.library_name}`}>
                {r.library_name}
              </div>
              {r.note ? <div className="line-clamp-3 text-[11px] text-fg-faint">{r.note}</div> : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3">
          <Empty>Референсов пока нет.</Empty>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Hint>добавить из библиотеки:</Hint>
        <select value={libraryId} onChange={(e) => setLibraryId(Number(e.target.value))} className={selectClass}>
          <option value={0}>— выбрать библиотеку —</option>
          {groupInOrder(libraries, (l) => l.category_name).map(([category, libs]) => (
            <optgroup key={category} label={category}>
              {libs.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.photos})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <ErrorLine text={error} className="mt-2" />

      {libraryId ? (
        <RefPicker
          libraryId={libraryId}
          picked={picked}
          busyItem={attach.isPending ? (attach.variables ?? null) : null}
          onPick={(itemId) => attach.mutate(itemId)}
        />
      ) : null}
    </Panel>
  );
}

/** Содержимое выбранной библиотеки (прежний `_ref_picker`): «взять в проект» или «уже в проекте». */
function RefPicker({
  libraryId,
  picked,
  busyItem,
  onPick,
}: {
  libraryId: number;
  picked: Set<number>;
  busyItem: number | null;
  onPick: (itemId: number) => void;
}) {
  const { data, isPending, error } = useQuery({
    queryKey: qk.library(libraryId),
    queryFn: () => apiFetch<LibraryDetail>(`/api/libraries/${libraryId}`),
  });

  if (isPending) return <Hint className="mt-2">загружаю библиотеку…</Hint>;
  if (error || !data) return <ErrorLine text={errorText(error)} className="mt-2" />;

  return (
    <div className="mt-3 rounded-[var(--radius-ui)] border border-border bg-bg p-3" data-annot={`выбор из библиотеки ${libraryId}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <Hint>
          {data.library.category_name} · {data.library.name} — {data.items.length} фото
        </Hint>
        <Link to={`/lib/${libraryId}`} className="inline-flex items-center gap-1 text-[11.5px] text-accent-text hover:underline">
          открыть библиотеку <ExternalLink size={11} />
        </Link>
      </div>
      {data.items.length ? (
        <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2">
          {data.items.map((i) => {
            const taken = picked.has(i.id);
            return (
              <div key={i.id} className={clsx("grid gap-1", taken && "opacity-60")}>
                <img
                  src={thumbUrl(i.asset_path)}
                  alt={i.note ?? "референс"}
                  loading="lazy"
                  className={clsx(
                    "aspect-[3/4] w-full rounded-[4px] border object-cover",
                    taken ? "border-accent" : "border-border",
                  )}
                />
                {i.note ? <div className="line-clamp-2 text-[11px] text-fg-faint">{i.note}</div> : null}
                {taken ? (
                  <div className="text-[11.5px] text-accent-text">уже в проекте</div>
                ) : (
                  <button
                    type="button"
                    disabled={busyItem === i.id}
                    onClick={() => onPick(i.id)}
                    className="inline-flex items-center gap-1 text-left text-[11.5px] text-accent-text hover:underline disabled:opacity-50"
                  >
                    <Plus size={11} /> взять в проект
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-2">
          <Empty>В этой библиотеке пока нет фотографий.</Empty>
        </div>
      )}
    </div>
  );
}
